import * as React from "react";
import {useRef, useState} from "react";
import {Trans, useTranslation} from "react-i18next";
import {Button, ButtonStyles, ButtonType} from "../../../../lib/form/Button/Button";
import Spinner from "../../../../utils/loader/Spinner";
import {CheckoutError, LoginMode} from "../../../Checkout";
import {ICredentials} from "../../../../../typescript-library";
import styles from "./KnownCustomer.module.scss";
import {useAppDispatch, useCustomerSelector} from "../../../../../store";
import {loginCustomer} from "../../../../../store/slices/customerSlice";
import ForgotPasswordModal from "../../../../lib/ModalWindow/ForgotPasswordModal";
import MagicLinkModal from "../../../../lib/ModalWindow/MagicLinkModal";

const KnownCustomer: React.FC<{onLoggedIn?: () => void}> = ({onLoggedIn}) => {
  const {customer} = useCustomerSelector();
  const dispatch = useAppDispatch();
  const {t} = useTranslation();
  const [{forgotVisible, magicVisible}, setModals] = useState<{
    forgotVisible: boolean;
    magicVisible: boolean;
  }>({forgotVisible: false, magicVisible: false});

  const buttonText = t("customer.logIn");
  const emailInputRef = useRef<HTMLInputElement>(null);
  const passwordInputRef = useRef<HTMLInputElement>(null);
  const [{buttonState, errors}, setState] = React.useState<{buttonState: string | JSX.Element; errors: string[]}>({
    buttonState: buttonText,
    errors: [],
  });

  const email = customer.email || undefined;
  const login = async (evt: React.MouseEvent<HTMLButtonElement>) => {
    evt.preventDefault();

    setState({buttonState: <Spinner />, errors: []});
    const credentials: ICredentials = {
      password: `${passwordInputRef.current?.value || ""}`,
      username: `${emailInputRef.current?.value || ""}`,
    };
    const loginErrors: string[] = [];
    if (!credentials.username) {
      loginErrors.push(t("form.emailOrCustomerNumberMissing"));
    }
    if (!credentials.password) {
      loginErrors.push(t("form.passwordMissing"));
    }
    if (loginErrors.length) {
      setState({buttonState: buttonText, errors: loginErrors});

      return;
    }

    await dispatch(loginCustomer(credentials)).then(({payload}) => {
      const success = payload as boolean;
      if (!success) {
        if (passwordInputRef.current) {
          passwordInputRef.current.value = "";
        }
        setState({buttonState: buttonText, errors: [t("form.emailOrPasswordIncorrect")]});
      } else {
        if (onLoggedIn) {
          onLoggedIn();
        }
      }
    });
  };

  return (
    <div className={styles.wrapper}>
      <h3 className={styles.headline}>{t("form.pleaseLogIn")}</h3>
      <CheckoutError>{errors}</CheckoutError>
      <form method="post" className={styles.formList}>
        <div>
          <label htmlFor="LoginEmail">{t("form.yourEmailOrCustomerNo")}</label>
          <input type="text" ref={emailInputRef} name="Email" id="LoginEmail" defaultValue={email} />
        </div>
        <div>
          <label htmlFor="Password">{t("form.yourPassword")}</label>
          <input type="password" name="Password" id="Password" ref={passwordInputRef} />
        </div>
        <Button
          btnIdentifier={LoginMode.KNOWNCUSTOMER}
          action={login}
          type={typeof buttonState === "string" ? ButtonType.default : ButtonType.disabled}
          styles={[ButtonStyles.wide]}
        >
          {buttonState}
        </Button>
      </form>
      {/* Region: Modals */}
      <ForgotPasswordModal
        isVisible={forgotVisible}
        closeFunction={() => setModals({forgotVisible: false, magicVisible})}
      />
      <MagicLinkModal
        givenEmail={emailInputRef.current?.value || customer.email || ""}
        isVisible={magicVisible}
        closeFunction={() => setModals({forgotVisible, magicVisible: false})}
      />
      {/* endregion: Modals */}
      <p>
        <Trans
          i18nKey="general.forgotPassword"
          ns="translations"
          components={{
            a: <span />,
            b: <br />,
            c: (
              <a
                href="#"
                onClick={(evt: React.MouseEvent<HTMLAnchorElement>) => {
                  evt.preventDefault();
                  setModals({forgotVisible: true, magicVisible});
                }}
              />
            ),
            d: (
              <a
                href="#"
                onClick={(evt: React.MouseEvent<HTMLAnchorElement>) => {
                  evt.preventDefault();
                  setModals({forgotVisible, magicVisible: true});
                }}
              />
            ),
          }}
        />
      </p>
    </div>
  );
};

export default KnownCustomer;
